import * as React from "react";
import {Form, FormListener} from "../form/Form";
import {SelectInput} from "../form/SelectInput";
import {FormInput} from "../form/FormInput";
import {Validators} from "../form/Validators";
import {Submit} from "../form/Submit";
import {FormSubmitError, FormSubmitSuccess} from "../form/FormFeedback";
import {Collapsible} from "../util/Collapsible";
import {WalletBalanceResult, WalletTransactionRequest} from "../../service/admin/WalletService";
import { reasonCategoryConfig } from '../dropdown-options';

interface Props {
    pokUserId: string | number;
    credit: (request: WalletTransactionRequest) => Promise<WalletBalanceResult>;
    debit: (request: WalletTransactionRequest) => Promise<WalletBalanceResult>;
    onAdjustmentComplete: (account: string, result: WalletBalanceResult) => void;
}

interface State {
    transactionForm?: TransferForm;
    validationMap?: any;
}

export class UserTransactionPanel extends React.Component<Props, State> implements FormListener<TransferForm> {

    private accountCurrencyCodeChanged = false;
    private transactionTypeChanged = false
    private reasonChanged = false

    constructor(props) {
        super(props);
        this.state = {transactionForm: {}, validationMap: {}};
    }

    onSubmit(): Promise<any> {
        let submitFn;
        if (this.state.transactionForm.type === 'ADDITION') {
            submitFn = this.props.credit;
        } else if (this.state.transactionForm.type === 'REMOVAL') {
            submitFn = this.props.debit;
        } else {
            return;
        }
        var request: WalletTransactionRequest = {
            userId: +this.props.pokUserId,
            amount: '' + this.state.transactionForm.amount,
            comment: this.state.transactionForm.comment,
            accountType: this.state.transactionForm.accountType,
            reasonCode: this.state.transactionForm.manualAdjustmentReasonCode,
        };
        return submitFn(request).then((tr) => {
            // reset form
            this.setState({
                transactionForm: {
                    ...this.state.transactionForm,
                    amount: undefined,
                    manualAdjustmentReasonCode: "",
                    comment: ""
                },
            })
            this.props.onAdjustmentComplete(request.accountType, tr);
        }).catch(err => {
            throw err.message;
        });
    }

    formDidUpdate(formModel: TransferForm, validationMap?: any) {
        if (this.transactionTypeChanged) {
            const { type, manualAdjustmentReasonCode } = formModel
            // we reacting to a type change here (debit->credit or vice-versa), and some reasons do not
            // apply for both. If that's the case, we set a blank value for reason and reset form validation
            if (Object.keys(reasonCategoryConfig[type]).indexOf(manualAdjustmentReasonCode) < 0) {
                // empty value
                formModel = {
                    ...formModel,
                    manualAdjustmentReasonCode: '',
                }
                // review manualAdjustmentReasonCode validation
                validationMap = {
                    ...validationMap,
                    manualAdjustmentReasonCode: {
                        ...validationMap.manualAdjustmentReasonCode,
                        displayError: true,
                        errorName: 'required',
                    }
                }
            }
            this.transactionTypeChanged = false
        }
        if (this.reasonChanged) {
            const { type, manualAdjustmentReasonCode } = formModel
            const selectedTypeReasons = reasonCategoryConfig[type];
            const reasonData = selectedTypeReasons[manualAdjustmentReasonCode];
            // set draft comment
            formModel = {
                ...formModel,
                comment: reasonData.commentDraft
            }
            // remove comment validation
            validationMap = {
                ...validationMap,
                comment: {
                    ...validationMap.comment,
                    displayError: false,
                    errorName: null,
                }
            }
            this.reasonChanged = false
        }
        this.setState({transactionForm: formModel, validationMap: validationMap});
    }

    toDisplayValue(type: string) {
        switch (type) {
            case 'sc-main': return 'SC';
            case 'sc-vault': return 'SC Vault';
            case 'gc-main': return 'GC';
            case 'gc-vault': return 'GC Vault';
        }
    }

    isAccountCurrencyCodeXSD() {
        const accountType =  this.state.transactionForm.accountType
        return accountType === 'sc-main' || accountType === 'sc-vault';
    }

    getAmountValidators() {
        if (this.isAccountCurrencyCodeXSD()) {
            return [Validators.required(), Validators.number(), Validators.isLessThan(10001)]
        }
        return [Validators.required(), Validators.number()]
    }

    getAmountLimitWarning() {
        if (this.isAccountCurrencyCodeXSD()) {
            return <span>Max 10,000 XSD</span>
        }
        return <span/>
    }

    amountFieldChanged = () => {
        this.accountCurrencyCodeChanged = true;
    }

    onTransactionTypeChange = () => {
        this.transactionTypeChanged = true
    }

    onReasonChanged = () => {
        this.reasonChanged = true
    }

    getForm() {
        let reasonCategoriesForSelection = reasonCategoryConfig[this.state.transactionForm.type] || {}

        return (
            <Form className="manual-adjustment-form container-fluid" formListener={this}
                  validationMap={this.state.validationMap} modelObject={this.state.transactionForm}>
                <div className="row">
                    <div className="col-sm-12">
                        <label>{this.state.transactionForm.type == "ADDITION" ? "To" : "From"} Account</label>
                        <SelectInput className="form-control" model="accountType" values={['sc-main', 'sc-vault', 'gc-main', 'gc-vault']}
                                     displayValue={this.toDisplayValue} onChange={this.amountFieldChanged}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-6">
                        <label>Transaction Type</label>
                        <SelectInput className="form-control" model="type" values={["ADDITION", "REMOVAL"]}
                                     onChange={this.onTransactionTypeChange}/>
                    </div>
                    <div className="col-sm-6">
                        <label>Amount:</label>
                        <FormInput className="form-control" model="amount" type="float" style={{marginBottom: "5px"}}
                                   validators={this.getAmountValidators()}/>
                        {this.getAmountLimitWarning()}
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <label>Reason</label>
                        <SelectInput className="form-control" model="manualAdjustmentReasonCode"
                                     validators={[Validators.required()]}
                                     values={Object.keys(reasonCategoriesForSelection)}
                                     displayValue={(v) => reasonCategoriesForSelection[v].reasonDisplay}
                                     onChange={this.onReasonChanged}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-12">
                        <label>Comment</label>
                        <FormInput type="text" className="form-control" model="comment"
                                   validators={[Validators.required(), Validators.textInput()]}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-8">

                    </div>
                    <div className="col-sm-4">
                        <label>&nbsp;</label>
                        <FormSubmitError/>
                        <FormSubmitSuccess text={"Transfer successful!"}/>
                        <Submit className="btn btn-primary btn-block">Transfer</Submit>
                    </div>
                </div>
            </Form>
        );
    }

    render() {
        // Clear amount field if account selection has changed
        // Required as we can't run validators on demand
        if (this.accountCurrencyCodeChanged) {
            this.setState({transactionForm: Object.assign({}, this.state.transactionForm, {amount: ""})});
            this.accountCurrencyCodeChanged = false;
        }

        return (
            <div className="col-sm-12">
                <Collapsible title="Manual Balance Adjustment" collapsed={true}>
                    {this.getForm()}
                </Collapsible>
            </div>
        );
    }
}

interface TransferForm {
    accountType?: 'sc-main' | 'sc-vault' | 'gc-main' | 'gc-vault';
    type?: 'ADDITION' | 'REMOVAL';
    amount?: number;
    manualAdjustmentReasonCode?: string;
    comment?: string;
}



// WEBPACK FOOTER //
// ./src/app/ui/user/UserTransactionPanel.tsx